/**
 * REST seller endpoints — DOC1 §7.6 Part A, §11.1.
 *
 *   POST /seller/auth/otp/request   — seller OTP login (same rules as user OTP)
 *   POST /seller/auth/otp/verify    — verify + receive seller JWT
 *   POST /seller/devices/register   — pre-register a device to a customer mobile
 */
import { z } from 'zod';
import { E164MobileSchema } from '../common/mobile.js';
import { Iso8601Schema } from '../common/timestamp.js';
import { UuidSchema } from '../common/id.js';
// ── seller auth (same OTP discipline as user auth) ─────────────────────────
export const SellerLoginRequestBody = z.object({
    mobileNumber: E164MobileSchema,
    name: z.string().min(1).max(120).optional(),
});
export const SellerLoginResponse = z.object({
    resendAvailableAt: Iso8601Schema,
});
export const SellerVerifyBody = z.object({
    mobileNumber: E164MobileSchema,
    otp: z.string().length(6).regex(/^\d{6}$/),
    name: z.string().min(1).max(120).optional(),
});
export const SellerVerifyResponse = z.object({
    accessToken: z.string().min(1),
    refreshToken: z.string().min(1),
    expiresIn: z.number().int().positive(),
    refreshExpiresIn: z.number().int().positive(),
    seller: z.object({
        id: UuidSchema,
        mobile: E164MobileSchema,
        name: z.string().min(1),
        status: z.enum(['active', 'blocked']),
    }),
});
// ── POST /seller/auth/refresh ───────────────────────────────────────────────
// Seller realm has its own refresh endpoint: seller refresh tokens live under
// the seller_refresh:{familyId} Redis namespace, invisible to /auth/refresh.
export const SellerRefreshBody = z.object({
    refreshToken: z.string().min(1),
});
export const SellerRefreshResponse = z.object({
    accessToken: z.string().min(1),
    expiresIn: z.number().int().positive(),
});
// ── POST /seller/devices/register ──────────────────────────────────────────
export const SellerRegistrationStatusSchema = z.enum([
    'pending',
    'activated',
    'claimed',
    'superseded',
]);
export const ProductTypeSchema = z.enum(['cop_lop', 'm8', 'avira']);
export const InvoiceUploadSchema = z.object({
    base64: z.string().min(1),
    filename: z.string().min(1).max(255),
    mimeType: z.enum(['application/pdf', 'image/jpeg', 'image/png']),
});
// PUT /seller/devices/:id/invoice — re-upload after the original registration.
// Same shape as the upload body in SellerRegisterBody.invoice; promoted to its
// own name so call-sites and docs are unambiguous.
export const InvoiceReplaceBody = InvoiceUploadSchema;
export const InvoiceReplaceResponse = z.object({
    invoiceFilename: z.string(),
    invoiceMimeType: z.string(),
    invoiceUploadedAt: z.string(),
});
export const SellerRegisterBody = z.object({
    serialNumber: z.string().min(1).max(64),
    customerMobile: E164MobileSchema,
    customerName: z.string().min(1).max(120).optional(),
    customerAddress: z.string().max(255).optional(),
    installerMobile: E164MobileSchema.optional(),
    installerName: z.string().max(120).optional(),
    productType: ProductTypeSchema.optional(),
    invoice: InvoiceUploadSchema.optional(),
}).superRefine((val, ctx) => {
    const product = val.productType ?? 'cop_lop';
    if (product === 'cop_lop' && !val.invoice) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Invoice is required for COP-LOP (lift) registrations.',
            path: ['invoice'],
        });
    }
});
export const SellerRegisterResponse = z.object({
    registrationId: UuidSchema,
    status: SellerRegistrationStatusSchema,
});
// ── GET /seller/devices ────────────────────────────────────────────────────
export const SellerRegistrationItemSchema = z.object({
    registrationId: UuidSchema,
    serialNumber: z.string().min(1).max(64),
    customerMobile: E164MobileSchema,
    customerName: z.string().nullable().optional(),
    customerAddress: z.string().nullable().optional(),
    installerMobile: z.string().nullable().optional(),
    installerName: z.string().nullable().optional(),
    productType: ProductTypeSchema.optional(),
    status: SellerRegistrationStatusSchema,
    registeredAt: Iso8601Schema,
    warrantyStartAt: Iso8601Schema.nullable().optional(),
    warrantyExpiresAt: Iso8601Schema.nullable().optional(),
    invoiceFilename: z.string().nullable().optional(),
    invoiceMimeType: z.string().nullable().optional(),
    invoiceUploadedAt: Iso8601Schema.nullable().optional(),
});
export const SellerDevicesResponse = z.object({
    registrations: z.array(SellerRegistrationItemSchema),
});
// ── GET /seller/devices/:id ────────────────────────────────────────────────
export const SellerRegistrationDetailResponse = z.object({
    registration: SellerRegistrationItemSchema,
});
// ── Service requests — POST /seller/devices/:id/service ───────────────────
//                       GET  /seller/devices/:id/service
export const ServiceRequestBody = z.object({
    problem: z.string().min(1).max(1000),
});
export const ServiceRequestStatusSchema = z.enum(['open', 'resolved']);
export const ServiceRequestItem = z.object({
    id: UuidSchema,
    problem: z.string(),
    status: ServiceRequestStatusSchema,
    createdAt: Iso8601Schema,
});
export const ServiceRequestResponse = z.object({
    id: UuidSchema,
    status: ServiceRequestStatusSchema,
    createdAt: Iso8601Schema,
});
export const ServiceRequestsResponse = z.object({
    requests: z.array(ServiceRequestItem),
});
// ── GET /seller/services — all requests across all devices ─────────────────
export const SellerServiceListItem = z.object({
    id: UuidSchema,
    registrationId: UuidSchema,
    serialNumber: z.string(),
    problem: z.string(),
    status: ServiceRequestStatusSchema,
    createdAt: Iso8601Schema,
});
export const SellerServicesResponse = z.object({
    requests: z.array(SellerServiceListItem),
});
// ── PATCH /seller/services/:requestId/resolve ──────────────────────────────
export const ResolveServiceRequestResponse = z.object({
    id: UuidSchema,
    status: ServiceRequestStatusSchema,
    updatedAt: Iso8601Schema,
});
// ── GET /seller/dashboard ──────────────────────────────────────────────────
export const SellerDashboardResponse = z.object({
    total: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
    warrantyExpiringSoon: z.number().int().nonnegative(),
    openComplaints: z.number().int().nonnegative(),
});
// ── Installers — GET/POST/PATCH/DELETE /seller/installers ─────────────────
export const SellerInstallerSchema = z.object({
    id: UuidSchema,
    name: z.string().min(1).max(120),
    mobile: E164MobileSchema,
    serviceArea: z.string().max(120).nullable(),
    experience: z.string().max(120).nullable(),
    active: z.boolean(),
    createdAt: Iso8601Schema,
});
export const SellerInstallersResponse = z.object({
    installers: z.array(SellerInstallerSchema),
});
export const CreateInstallerBody = z.object({
    name: z.string().min(1).max(120),
    mobile: E164MobileSchema,
    serviceArea: z.string().max(120).optional(),
    experience: z.string().max(120).optional(),
});
export const UpdateInstallerBody = CreateInstallerBody.partial().extend({
    active: z.boolean().optional(),
});
// ── Portal admins — GET/POST/DELETE /seller/portal-admins ────────────────
export const SellerPortalAdminSchema = z.object({
    id: UuidSchema,
    name: z.string().min(1).max(120),
    mobile: E164MobileSchema,
    addedAt: Iso8601Schema,
});
export const SellerPortalAdminsResponse = z.object({
    admins: z.array(SellerPortalAdminSchema),
});
export const AddPortalAdminBody = z.object({
    name: z.string().min(1).max(120),
    mobile: E164MobileSchema,
});
// ── Product selection — GET/PUT /seller/products ──────────────────────────
export const SellerProductsResponse = z.object({
    selectedProducts: z.array(ProductTypeSchema),
});
export const SetSellerProductsBody = z.object({
    selectedProducts: z.array(ProductTypeSchema),
});
//# sourceMappingURL=seller.js.map